import { ColumnSettings, getBuiltinColumnSettings } from './built-in-columns-settings';
import {
  BaseColumn,
  ColumnIdentifier,
  ColumnType,
  FloatColumn,
  FloatSeriesColumn,
  LeaderboardColumn,
  LeaderboardColumnCreationParams,
} from './column-model';
import { Reorderable } from './types';

export function createColumnByColumnIdentifier(
  columnIdentifier: ColumnIdentifier,
  pinned = false,
): LeaderboardColumn {
  const builtinColumnSettings = getBuiltinColumnSettings(columnIdentifier);

  if (builtinColumnSettings != null) {
    return leaderboardColumnFactory({ ...builtinColumnSettings, pinned });
  }

  return leaderboardColumnFactory({
    ...columnIdentifier,
    pinned,
  });
}

type LeaderboardColumnFactoryParam = LeaderboardColumnCreationParams & {
  getColumnSettings?: ({ id, columnType }: ColumnIdentifier) => ColumnSettings | undefined;
};

export function leaderboardColumnFactory(
  factoryParams: LeaderboardColumnFactoryParam,
): LeaderboardColumn {
  switch (factoryParams.columnType) {
    case 'float':
      return floatColumnFactory(factoryParams);

    case 'floatSeries':
      return floatSeriesColumnFactory(factoryParams);

    default:
      return baseLeaderboardColumnFactory(factoryParams);
  }
}

function floatColumnFactory(factoryParams: LeaderboardColumnFactoryParam): FloatColumn {
  const { getColumnSettings, ...params } = factoryParams;
  const baseColumn = baseLeaderboardColumnFactory(factoryParams);

  return {
    ...baseColumn,
    columnType: 'float',
    displayMode: params.displayMode || 'decimal',
  };
}

function floatSeriesColumnFactory(factoryParams: LeaderboardColumnFactoryParam): FloatSeriesColumn {
  const { getColumnSettings, ...params } = factoryParams;
  const baseColumn = baseLeaderboardColumnFactory(params);

  return {
    ...baseColumn,
    columnType: 'floatSeries',
    displayMode: params.displayMode || 'decimal',
    aggregationMode: params.aggregationMode || 'last',
  };
}

function baseLeaderboardColumnFactory({
  id,
  columnType,
  attributeName,
  displayName,
  color,
  getColumnSettings = getBuiltinColumnSettings,
  ...rest
}: LeaderboardColumnFactoryParam): BaseColumn {
  const { pinned = false, width = 100 } = rest;

  const builtInColumnSettings = getColumnSettings({ id, columnType });

  const groupable = calculateGroupable(columnType, builtInColumnSettings?.groupable);
  const hideable = calculateHideable(columnType, builtInColumnSettings?.hideable);
  const resizable = calculateResizable(builtInColumnSettings?.resizable);
  const sortable = calculateSortable(columnType, builtInColumnSettings?.sortable);
  const reorderable = calculateReorderable(columnType, builtInColumnSettings?.reorderable);
  const exportable = calculateExportable(columnType, builtInColumnSettings?.exportable);
  const calculatedWidth = calculateWidth(width, builtInColumnSettings?.width, resizable);
  const renamable = calculateRenamable(builtInColumnSettings?.renamable);
  const recolorable = calculateRecolorable(builtInColumnSettings?.recolorable);

  return {
    id,
    columnType,
    attributeName,
    color,
    displayName,
    exportable,
    groupable,
    hideable,
    pinned,
    reorderable,
    resizable,
    sortable,
    renamable,
    recolorable,
    autoWidthEnabled: builtInColumnSettings?.autoWidthEnabled,
    width: calculatedWidth,
  };
}

const groupableColumnTypes: ColumnType[] = [
  'bool',
  'experimentState',
  'float',
  'floatSeries',
  'gitRef',
  'int',
  'string',
  'stringSeries',
  'stringSet',
];

function calculateGroupable(columnType: ColumnType, initial?: boolean): boolean {
  if (initial != null) {
    return initial;
  }

  return groupableColumnTypes.includes(columnType);
}

function calculateHideable(columnType: ColumnType, initial?: boolean): boolean {
  return initial != null ? initial : true;
}

function calculateSortable(columnType: ColumnType, initial?: boolean): boolean {
  return initial != null
    ? initial
    : [
        'bool',
        'datetime',
        'experimentState',
        'float',
        'floatSeries',
        'int',
        'string',
        'stringSeries',
      ].includes(columnType);
}

function calculateReorderable(columnType: ColumnType, initial?: Reorderable): Reorderable {
  return initial != null ? initial : true;
}

function calculateResizable(initial?: boolean): boolean {
  return initial != null ? initial : true;
}

function calculateRecolorable(initial?: boolean): boolean {
  return initial != null ? initial : true;
}

function calculateRenamable(initial?: boolean): boolean {
  return initial != null ? initial : true;
}

function calculateExportable(columnType: ColumnType, initial?: boolean): boolean {
  return initial != null ? initial : columnType !== 'notSupported';
}

function calculateWidth(width: number, initial?: number, resizable?: boolean): number {
  // When the column is not resizable, we should always return it's initial width.
  // This enables us to change the width of the column in the config if we need to.
  if (!resizable) {
    return initial || width;
  }

  return width;
}
